/**
 * hook for managing page number input field interactions
 * handles editing state, validation, and keyboard navigation
 */
import { useCallback, useRef, useState } from "react";

import { METADOC_FIRST_PAGE } from "../../MetadocConstants";
import type { RefObject } from "react";

type UsePageInputParams = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageJump?: () => void;
  minPage?: number;
  maxPage?: number;
  visiblePageNumbers?: number[];
};

type UsePageInputReturn = {
  pageInput: string;
  isEditing: boolean;
  inputRef: RefObject<HTMLInputElement>;
  handlers: {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  };
  focusPageInput: () => void;
};

export const usePageInput = ({
  currentPage,
  totalPages,
  onPageChange,
  onPageJump,
  minPage = METADOC_FIRST_PAGE,
  maxPage = totalPages,
  visiblePageNumbers,
}: UsePageInputParams): UsePageInputReturn => {
  const [pageInput, setPageInput] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const visiblePageNumberSet = visiblePageNumbers
    ? new Set(visiblePageNumbers)
    : null;

  const handlePageInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPageInput(e.target.value);
    },
    [],
  );

  const handlePageInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditing(true);
      // ensure the input has the current page value when focused, specifically for programmatic focus
      setPageInput(currentPage.toString());
      e.target.select();
    },
    [currentPage],
  );

  const handlePageInputBlur = useCallback(() => {
    setIsEditing(false);
    const pageNum = parseInt(pageInput, 10);
    const isVisiblePage =
      visiblePageNumberSet === null || visiblePageNumberSet.has(pageNum);

    if (
      !isNaN(pageNum) &&
      pageNum >= minPage &&
      pageNum <= maxPage &&
      isVisiblePage
    ) {
      if (pageNum !== currentPage) {
        onPageJump?.();
      }
      onPageChange(pageNum);
    }
    setPageInput("");
  }, [
    currentPage,
    maxPage,
    minPage,
    onPageChange,
    onPageJump,
    pageInput,
    visiblePageNumberSet,
  ]);

  const handlePageInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        const pageNum = parseInt(pageInput, 10);
        const isVisiblePage =
          visiblePageNumberSet === null || visiblePageNumberSet.has(pageNum);

        if (
          !isNaN(pageNum) &&
          pageNum >= minPage &&
          pageNum <= maxPage &&
          isVisiblePage
        ) {
          if (pageNum !== currentPage) {
            onPageJump?.();
          }
          onPageChange(pageNum);
        }
        setPageInput("");
        setIsEditing(false);
        e.currentTarget.blur();
      }
    },
    [
      currentPage,
      maxPage,
      minPage,
      onPageChange,
      onPageJump,
      pageInput,
      visiblePageNumberSet,
    ],
  );

  const focusPageInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  return {
    pageInput,
    isEditing,
    inputRef,
    handlers: {
      onChange: handlePageInputChange,
      onFocus: handlePageInputFocus,
      onBlur: handlePageInputBlur,
      onKeyDown: handlePageInputKeyDown,
    },
    focusPageInput,
  };
};
